import React, { useState, useEffect, ReactNode, useCallback } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Edit, Delete, Check } from '@mui/icons-material';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase/firebase';

// To use this you will need these:
// const [picChangePlaceHolder, setPicChangePlaceHolder] = useState<File | undefined>(undefined);

// Three cases you need to handle in every image change:
// 1. Initial image is undefined, and you add a new image (straightforward, will just update the imagePath with new image)
// 2. Initial image is defined, and you add a new image (straightforward, will just update the imagePath with new image)
// 3. Initial image is defined, and you remove the image:
//     Check to see if the old object's imagePath is defined,
//     while the new object's imagePath is not. if it is,
//     then delete the image from storage)

// See example in editableService card for how to use this component

interface ImageSelectProps {
    initialImagePath?: string; // Optional: If you want to display an image initially
    onImageChange: (imageFile?: File) => void;
    enabled?: boolean; // To disable the image selection
    allowDelete?: boolean; // To allow the user to delete the image
    placeholderComponent?: ReactNode; // Custom component to show above the button when no image is selected
    styleType?: 'portrait' | 'landscape' | 'circle';
    darkBackground?: boolean; // To set the background color of the image to black if true
    altText?: string;
    circleSize?: number; // Diameter of the 'circle' style, in px
}
const ImageSelect: React.FC<ImageSelectProps> = ({
    initialImagePath,
    onImageChange,
    enabled = true,
    allowDelete = true,
    placeholderComponent,
    styleType = 'circle',
    darkBackground = false,
    altText = 'Service Image',
    circleSize = 200,

}) => {
    const [imagePath, setImagePath] = useState<string | null>(null);
    const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
    const [isFetchingImageUrl, setIsFetchingImageUrl] = useState<boolean>(false);
    const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

    const handleFetchingImage = useCallback(async (imagePath: string) => {
        try {
            setIsFetchingImageUrl(true);
            setIsImageLoaded(false);
            setImagePath(null);
            const fullImagePath = await getDownloadURL(ref(storage, imagePath));
            setImagePath(fullImagePath);
        } catch (error) {
            // TODO: Theres a case when you submit to delete the image and it tries to fetch it again
            // console.error('Error fetching image:', error);
            setImagePath(null);
            setIsImageLoaded(false);
        } finally {
            setIsFetchingImageUrl(false);
        }
    }, []);

    const resetImageToInitialState = useCallback(() => {
        if (initialImagePath) {
            handleFetchingImage(initialImagePath);
        } else {
            setImagePath(null);
            setIsImageLoaded(false);
            setIsFetchingImageUrl(false);
        }
    }, [initialImagePath, handleFetchingImage]);

    useEffect(() => {
        resetImageToInitialState();
    }, [resetImageToInitialState]);

    useEffect(() => {
        if (!enabled) {
            resetImageToInitialState();
        }
    }, [enabled, resetImageToInitialState]);

    // handleFetchingImage moved above with useCallback

    const handleAddImage = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];

            if (!validImageTypes.includes(file.type)) {
                alert('Please upload a valid image file (JPEG, JPG, PNG).');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                setIsFetchingImageUrl(false);
                setIsImageLoaded(false);
                setImagePath(e.target?.result as string);
                onImageChange(file);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setImagePath(null);
        onImageChange(undefined);
        closeImageDialog();
    };

    const openImageDialog = () => {
        setIsImageDialogOpen(true);
    };

    const closeImageDialog = () => setIsImageDialogOpen(false);
    const isLoading = isFetchingImageUrl || (Boolean(imagePath) && !isImageLoaded);

    const getBoxStyle = () => {
        switch (styleType) {
            case 'portrait':
                return {
                    width: "100%",
                    maxHeight: "500px",
                    aspectRatio: "3 / 4",
                };
            case 'landscape':
                return {
                    width: "100%",
                    maxHeight: "300px",
                    aspectRatio: "4 / 3",
                };
            case 'circle':
                return {
                    width: `${circleSize}px`,
                    height: `${circleSize}px`,
                    borderRadius: "50%",
                    overflow: "hidden",
                    aspectRatio: '1 / 1'
                };
            default:
                return {};
        }
    };

    return (
        <Box display="flex" flexDirection="column" alignItems="center" sx={{ marginY: 2 }}>
            {imagePath || isLoading ? (
                <>
                    <Button
                        onClick={openImageDialog}
                        disabled={!enabled}
                        sx={{
                            ...getBoxStyle(),
                            padding: 0,
                            overflow: 'hidden',
                            backgroundColor: darkBackground ? '#000' : '#fff',
                            position: 'relative',
                        }}
                    >
                        {imagePath && (
                            <img
                                src={imagePath || undefined}
                                alt={altText}
                                onLoad={() => setIsImageLoaded(true)}
                                onError={() => {
                                    setImagePath(null);
                                    setIsImageLoaded(false);
                                }}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    backgroundColor: darkBackground ? '#000' : '#fff',
                                    opacity: isImageLoaded ? 1 : 0,
                                    transition: 'opacity 160ms ease',
                                }}
                            />
                        )}
                        {isLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                }}
                            >
                                <CircularProgress size={36} />
                            </Box>
                        )}
                    </Button>


                    {/* Image Dialog */}
                    <Dialog open={isImageDialogOpen} onClose={closeImageDialog} maxWidth="sm" fullWidth>
                        <DialogContent
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                padding: 3,
                            }}
                        >
                            {/* Image Display */}

                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: 400,
                                    borderRadius: 2,
                                    backgroundColor: darkBackground ? '#000' : '#fff',
                                    padding: 2,
                                    position: 'relative',
                                }}
                            >
                                {imagePath && (
                                    <img
                                        src={imagePath || undefined}
                                        alt={altText}
                                        onLoad={() => setIsImageLoaded(true)}
                                        onError={() => {
                                            setImagePath(null);
                                            setIsImageLoaded(false);
                                        }}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            opacity: isImageLoaded ? 1 : 0,
                                            transition: 'opacity 160ms ease',
                                        }}
                                    />
                                )}
                                {isLoading && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <CircularProgress size={36} />
                                    </Box>
                                )}
                            </Box>

                        </DialogContent>
                        <DialogActions>
                            <Button component="label" color="primary">
                                <Edit />
                                <input type="file" hidden onChange={handleAddImage} />
                                {/* Delete Button */}
                            </Button>
                            {allowDelete && <Button
                                onClick={handleRemoveImage}
                                color="error"
                            >
                                <Delete />
                            </Button>}
                            <Button
                                onClick={closeImageDialog}
                                color="primary"
                            >
                                <Check />
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            ) : (
                <>
                    {/* Display custom placeholder component if provided */}
                    {placeholderComponent && <Box mb={1}>{placeholderComponent}</Box>}

                    <Button variant="contained" component="label">
                        <AddIcon />
                        <input type="file" hidden onChange={handleAddImage} />
                    </Button>
                </>
            )}
        </Box>
    );
};

export default ImageSelect;
